import { HttpResponse } from 'msw'
import type {
  DefaultBodyType,
  HttpResponseResolver,
  JsonBodyType,
  PathParams,
} from 'msw'

/**
 * v1's `PathParams` had REQUIRED properties
 * (`{ [ParamName in KeyType]: string | ReadonlyArray<string> }`); v2 made
 * them optional, which would add `| undefined` to every `req.params.x` read
 * across the handlers and raise the typecheck ratchet. Pin the v1 shape so
 * `req.params` types exactly as it did under v1.
 */
export type V1PathParams = {
  [paramName: string]: string | ReadonlyArray<string>
}

/*
 * MSW v1 -> v2 compatibility adapter.
 *
 * WHY THIS EXISTS: msw@2 replaced the v1 resolver signature
 * `(req, res, ctx) => res(ctx.status(n), ctx.json(body))` with
 * `({ request, params, cookies }) => HttpResponse.json(body, { status })`.
 * This repo has ~82 mock-handler files written against the v1 idiom. Rather
 * than rewriting them all (high churn, high review cost), the wiring seam
 * (api/msw/api/index.ts makeHandler and the two other `rest`-using files)
 * wraps each v1 handler with `adaptV1Handler`, which reconstructs the small
 * v1 surface the handlers actually use. The handlers stay byte-for-byte on
 * the v1 idiom until a future dedicated rewrite.
 *
 * The v1 surface reproduced here is intentionally minimal — it is exactly
 * what a grep of api/msw shows in use, nothing more:
 *   ctx: only `ctx.status(code)` and `ctx.json(body)`
 *   req: `params`, `url` (as a URL object), `headers`, `method`, `cookies`,
 *        `json()`, and `body` (multipart auto-parse only — see below)
 *   res: `res(...transformers)` returning a Response; bodyless
 *        `res(ctx.status(200|201|204))` is supported
 *
 * The type names v1 exported (`RestRequest`, `RestContext`,
 * `ResponseFunction`) are re-added to the 'msw' module via the augmentation
 * in ./mswV1Compat.d.ts, aliasing the V1* types defined here.
 */

/**
 * Descriptor produced by the shimmed `ctx.status()` / `ctx.json()`.
 * In v1 these were composable response-patching functions; here they are
 * inert descriptors that `res(...)` folds into a single v2 `HttpResponse`.
 */
export type ResponseTransformer =
  | { readonly kind: 'status'; readonly statusCode: number }
  | { readonly kind: 'json'; readonly body: unknown }

/**
 * The v1 `req` shape the handlers consume. Mirrors v1's `RestRequest`
 * (a class in v1) closely enough that every existing usage type-checks
 * identically: `params` is `PathParams`, `url` is a `URL` object (handlers
 * use `.searchParams` / `.pathname`), and `json()` / `body` stay loose
 * (`any`) exactly as they were in v1.
 */
export type V1RestRequest = {
  readonly params: V1PathParams
  readonly url: URL
  readonly headers: Headers
  readonly method: string
  readonly cookies: Record<string, string>
  /**
   * v1 auto-parsed multipart/form-data bodies into a record of
   * field -> string | File (deprecated in v1, removed in v2). Only
   * parsePostedEntity reads this, and only behind a multipart content-type
   * guard, so the adapter populates it for multipart requests only.
   */
  readonly body: any
  json(): Promise<any>
}

/** The v1 `ctx` shape — only the two members the handlers use. */
export type V1RestContext = {
  status(statusCode: number): ResponseTransformer
  json(body: any): ResponseTransformer
}

/** The v1 `res` shape: folds transformer descriptors into a Response. */
export type V1ResponseFunction = (
  ...transformers: ResponseTransformer[]
) => Response

/** A v1-style response resolver, as written across api/msw. */
export type V1ResponseResolver = (
  req: V1RestRequest,
  res: V1ResponseFunction,
  ctx: V1RestContext
) => Response | Promise<Response>

const v1Context: V1RestContext = {
  status(statusCode: number): ResponseTransformer {
    return { kind: 'status', statusCode }
  },
  json(body: unknown): ResponseTransformer {
    return { kind: 'json', body }
  },
}

const v1Response: V1ResponseFunction = (...transformers) => {
  // Fold the descriptors: status defaults to 200 (v1's default), and a json
  // transformer, if present, supplies the body.
  let status = 200
  let hasJsonBody = false
  let jsonBody: unknown
  for (const transformer of transformers) {
    if (transformer.kind === 'status') {
      status = transformer.statusCode
    }
    if (transformer.kind === 'json') {
      hasJsonBody = true
      jsonBody = transformer.body
    }
  }
  if (hasJsonBody) {
    return HttpResponse.json(jsonBody as JsonBodyType, { status })
  }
  // Bodyless res(ctx.status(200|201|204)) sites.
  return new HttpResponse(null, { status })
}

/**
 * Wrap a v1-style `(req, res, ctx)` handler into an msw@2 response resolver.
 */
export function adaptV1Handler(
  v1Handler: V1ResponseResolver
): HttpResponseResolver<PathParams, DefaultBodyType, undefined> {
  return async ({ request, params, cookies }) => {
    // Reproduce v1's multipart auto-parse (v1's deprecated `req.body`), but
    // ONLY for multipart content — both `req.body` consumers (parsePostedEntity
    // and the case-reply handler) are guarded by this same content-type check.
    // A field name that REPEATS collects into an array, as v1 did: a case
    // reply sends one `files` part per attachment, and keeping only the last
    // would silently drop the rest. A name sent once stays a bare value, which
    // is what the document uploads (one binary part + one JSON `payload`
    // part) rely on.
    const contentType = request.headers.get('content-type') || ''
    let parsedBody: unknown
    if (contentType.includes('multipart/form-data')) {
      const formData = await request.clone().formData()
      // forEach rather than Object.fromEntries(formData.entries()): the
      // repo's TS lib set (DOM without DOM.Iterable) doesn't type
      // FormData.entries().
      const fields: Record<string, FormDataEntryValue | FormDataEntryValue[]> =
        {}
      formData.forEach((value, key) => {
        const existing = fields[key]
        fields[key] =
          existing === undefined
            ? value
            : Array.isArray(existing)
            ? [...existing, value]
            : [existing, value]
      })
      parsedBody = fields
    }

    const req: V1RestRequest = {
      // v2 types params' values as possibly-undefined (optional props);
      // v1 declared them required. Runtime contents are identical, so cast
      // back to the v1 typing the handlers were written against.
      params: params as V1PathParams,
      url: new URL(request.url),
      headers: request.headers,
      method: request.method,
      cookies,
      body: parsedBody,
      // Clone per call so json() is independent of the multipart pre-read
      // above and re-readable, like v1's pre-parsed body was.
      json: () => request.clone().json(),
    }

    // Await: some handlers return a Promise (e.g. the async
    // addToEntityListData path in mockDataCommon).
    return await v1Handler(req, v1Response, v1Context)
  }
}
